import { alpha, createTheme } from '@mui/material/styles';

export const backgroundColour = 'rgba(250, 253, 255, 1.0)';

export const palette = {
	folioBlue: '#505afc',
	folioPurple: '#5D24FF',
	folioPurpleAccent: '#5200CC',
	folioPurpleLight: '#E7DEFF',
	folioPurpleAccentLight: '#E5D9F7',
	folioPurpleMid: '#AE92FF',
	folioPurpleAccentMid: '#A980E6',
	folioGreen: '#5BFFA9',
	folioGreenDark: '#1f7549',
	folioTeal: '#C5F0EA',
	folioTealMid: '#6EE0D0',
	folioTealDark: '#1A7A6D',
	folioWarningMid: '#FFCA28',
	folioWarningHigh: '#FF3E3E',
	folioBlack100: '#343A40',
	folioRegular: '#343434',
	folioBlack65: 'rgba(52, 58, 64, 0.65)',
	folioBlack40: 'rgba(52, 58, 64, 0.4)',
	folioBlack25: 'rgba(52, 58, 64, 0.25)',
	folioBlack10: 'rgba(52, 58, 64, 0.1)',
	folioGrey: '#F4F4F7',
	folioWhite: '#FFFFFF',
	folioWhite65: 'rgba(255, 255, 255, 0.65)',
	folioWhite40: 'rgba(255, 255, 255, 0.4)',
	folioWhite25: 'rgba(255, 255, 255, 0.25)',
	folioWhite10: 'rgba(255, 255, 255, 0.1)',
	dialogActions: '#EFF0FF',
	sideNav: '#1C2536',
	sideNavItems: '#FAFDFF',
	folioBlueMid: '#A7ACFD',
	// Dark-mode surfaces, tuned to match the approved dark-mode mockup.
	darkModeBlue: 'rgb(144, 150, 246)',
	darkModeBackground: '#0a121f',
	darkModePaper: '#141b2d',
	darkModeSideNav: '#1c2536',
	darkModeTextField: '#1d2532',
	darkModeChip: '#2a336a',
	darkModeDivider: '#1e2738',
	darkModeHeader: '#0b1323',
	darkModeHover: '#14214d',
	// Winbeat colours
	winbeatNavy: '#0F1E33',
	winbeatOrange: '#FF3A17',
};

// Shared by the 'side-nav' / 'side-nav-winbeat' MuiListItemButton variants —
// only the accent colour differs between the two.
const sideNavListItemStyle = (theme, colour) => ({
	borderRadius: '10px',
	paddingTop: '4px',
	paddingBottom: '4px',
	transition: theme.transitions.create('color', { duration: 0 }),
	'&:hover': {
		backgroundColor: 'rgba(0, 0, 0, 0.1)',
		'&.Mui-selected': {
			backgroundColor: alpha(colour, 0.75),
		},
	},
	'&.Mui-selected': {
		backgroundColor: colour,
		border: `1px solid ${alpha(colour, 0.4)}`,
	},
	'& .MuiSvgIcon-root': {
		transition: `${theme.transitions.create('fill', { duration: 0 })} !important`,
	},
	'& .MuiListItemIcon-root': {
		transition: theme.transitions.create('color', { duration: 0 }),
	},
	'& path': {
		transition: theme.transitions.create(['fill', 'stroke'], {
			duration: 0,
		}),
	},
});

// Shared "minimal" input/label styling reused by many form fields —
// exported directly so those call sites don't need to import the full theme.
// Text/icon colour uses `primary.main` so it follows the light/dark palette.
export const minimalInputTheme = {
	backgroundColor: 'rgba(0,0,0,0)',
	color: 'primary.main',
	border: 'none', // Removes border
	'& .MuiInputBase-input': {
		fontWeight: 600, // Ensures input text is bold
	},
	'& .MuiSvgIcon-root': {
		color: 'primary.main', // Sets icon color
	},
	'&:focus': {
		backgroundColor: 'rgba(0,0,0,0)',
		borderColor: 'rgba(0,0,0,0)', // Removes border on focus
	},
};

export const minimalLabelTheme = {
	color: 'rgba(108, 115, 127, 1)',
	fontSize: '12px',
	fontWeight: 500,
};

const neutral = {
	100: '#F4F4F7',
	200: '#E4E7EB',
	300: '#CDD1D9',
	400: '#9AA2B1',
	500: '#6C737F',
	600: '#4D5461',
	700: '#37404F',
	800: '#232A35',
	900: '#161B22',
};

export const getAppTheme = (mode = 'light') => {
	const strongText = mode === 'dark' ? neutral[100] : '#000000';
	const primaryMain = mode === 'dark' ? palette.darkModeBlue : palette.folioBlue;

	return createTheme({
		palette: {
			...palette,
			mode,
			primary: {
				main: primaryMain,
			},
			neutral,
			sideNav: mode === 'dark' ? palette.darkModeSideNav : palette.sideNav,
			sideNavItems: palette.sideNavItems,
			...(mode === 'dark' && {
				background: {
					default: palette.darkModeBackground,
					paper: palette.darkModePaper,
				},
				divider: palette.darkModeDivider,
				action: {
					hover: palette.darkModeHover,
				},
			}),
		},
		typography: {
			fontFamily: 'Poppins',
			body1: {
				fontWeight: 400,
				fontSize: '14px',
			},
			broker_header: {
				fontWeight: 700,
				fontSize: '1em',
				color: strongText,
			},
			deal_header: {
				fontWeight: 700,
				fontSize: '24px',
				color: strongText,
			},
			card_header: {
				fontWeight: 400,
				fontSize: '14px',
				color: '#6C737F',
			},
			card_h4: {
				fontWeight: 700,
				fontSize: '32px',
				color: strongText,
			},
			form_label: {
				fontWeight: 600,
				fontSize: '18px',
				color: strongText,
			},
			avatar_title: {
				fontWeight: 500,
				fontSize: '14px',
				color: strongText,
			},
			avatar_subtitle: {
				fontWeight: 500,
				fontSize: '10px',
				color: '#5F5F5F',
			},
			task_body: {
				fontWeight: 400,
				fontSize: '14px',
				color: strongText,
			},
			task_updated: {
				fontWeight: 400,
				fontSize: '12px',
				color: mode === 'dark' ? 'rgba(244, 244, 247, 0.5)' : 'rgba(52, 73, 94, 0.5)',
			},
			details_header: {
				fontWeight: 500,
				fontSize: '18px',
				color: strongText,
			},
			details_body: {
				fontWeight: 400,
				fontSize: '14px',
				color: 'rgba(108, 115, 127, 1)',
			},
			blue_text: {
				fontWeight: 400,
				fontSize: '14px',
				color: primaryMain,
			},
			client_subheader: {
				fontWeight: 400,
				fontSize: '12px',
				color: strongText,
			},
			client_claims_table: {
				fontSize: '14px',
				fontWeight: 400,
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					':root': {
						'--folio-blue-text': primaryMain,
					},
					body: {
						fontFamily: "'Poppins', sans-serif",
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						lineHeight: 1.25,
					},
					containedPrimary: {
						borderRadius: '15px',
						// Keep the brand fill in dark mode; primary.main is lightened for text.
						...(mode === 'dark' && {
							backgroundColor: palette.folioBlue,
							color: palette.folioWhite,
						}),
						'&:hover': {
							backgroundColor: 'rgba(80, 90, 252, 0.75)', // 75% transparency
						},
					},
				},
				variants: [
					{
						props: { variant: 'tab_header' },
						style: ({ theme }) => ({
							...theme.components.MuiButton.styleOverrides.contained,
							borderRadius: '10px',
							backgroundColor: '#34495E',
							color: '#FFFFFF',
							boxShadow: theme.shadows[4], // Apply elevation shadow
							'&:hover': {
								backgroundColor: '#000000',
								boxShadow: theme.shadows[6], // Elevated shadow on hover
							},
							'&.Mui-disabled': {
								color: '#B0B7C3',
								backgroundColor: '#34495E',
								opacity: 0.8,
								boxShadow: 'none',
							},
						}),
					},
					{
						props: { variant: 'tab_header_outlined' },
						style: ({ theme }) => ({
							...theme.components.MuiButton.styleOverrides.outlined,
							borderRadius: '10px',
							border: '1px solid #34495E',
							color: '#34495E',
							'&:hover': {
								backgroundColor: '#ffffff',
								border: '1px solid #000000',
								color: '#000000',
								boxShadow: theme.shadows[1], // Elevated shadow on hover
							},
						}),
					},
					{
						props: { variant: 'edit_button' },
						style: ({ theme }) => ({
							...theme.components.MuiButton.styleOverrides.contained,
							borderRadius: '8px',
							backgroundColor: palette.folioBlue,
							color: '#FFFFFF',
							padding: '2px 24px',
							boxShadow: theme.shadows[4], // Apply elevation shadow
							'&:hover': {
								backgroundColor: 'rgba(80, 90, 252, 0.5)',
								boxShadow: theme.shadows[6], // Elevated shadow on hover
							},
						}),
					},
					{
						props: { variant: 'table_actions' },
						style: ({ theme }) => ({
							...theme.components.MuiButton.styleOverrides.contained,
							...theme.components.MuiButton.styleOverrides.containedPrimary,
							fontSize: '12px',
							textTransform: 'none',
							minHeight: '2.5em',
							lineHeight: 1.25,
							backgroundColor: palette.folioBlue,
							color: '#FFFFFF',
							padding: '4px 8px',
						}),
					},
				],
			},
			MuiLink: {
				styleOverrides: {
					root: {
						fontFamily: 'Poppins',
					},
				},
			},
			MuiInputBase: {
				styleOverrides: {
					root: {
						backgroundColor: mode === 'dark' ? palette.darkModeTextField : '#f5f8fa',
						borderRadius: '5px',
						'&.Mui-disabled': {
							backgroundColor:
								mode === 'dark' ? neutral[800] : 'rgba(224, 224, 224, 1)',
						},
					},
					minimal: minimalInputTheme,
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						minHeight: 35,
						'&:not(.MuiInputBase-multiline)': {
							height: 35,
						},
						'&.MuiInputBase-multiline': {
							height: 'auto',
							padding: '6px 14px',
						},
						'&:not(.MuiInputBase-multiline) .MuiOutlinedInput-input:not(.MuiSelect-select)':
							{
								padding: '6px 14px',
								boxSizing: 'border-box',
								height: '100%',
							},
						'&.MuiInputBase-multiline .MuiOutlinedInput-input': {
							padding: 0,
							height: 'auto',
						},
						'& .MuiSelect-select': {
							minHeight: 'unset',
							display: 'flex',
							alignItems: 'center',
							padding: '6px 32px 6px 14px',
							boxSizing: 'border-box',
						},
						'& .MuiIconButton-root': {
							padding: 4,
						},
					},
				},
			},
			MuiTextField: {
				styleOverrides: {
					root: {
						'& .MuiOutlinedInput-root': {
							minHeight: 35,
						},
						'& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': {
							height: 35,
						},
						'& .MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input:not(.MuiSelect-select)':
							{
								padding: '6px 14px',
								boxSizing: 'border-box',
								height: '100%',
							},
						'& .MuiOutlinedInput-root.MuiInputBase-multiline': {
							height: 'auto',
							padding: '6px 14px',
						},
						'& .MuiOutlinedInput-root.MuiInputBase-multiline .MuiOutlinedInput-input': {
							padding: 0,
							height: 'auto',
						},
					},
				},
			},
			MuiSelect: {
				styleOverrides: {
					select: {
						minHeight: 'unset',
						display: 'flex',
						alignItems: 'center',
						paddingTop: 6,
						paddingBottom: 6,
						boxSizing: 'border-box',
					},
				},
			},
			MuiCheckbox: {
				defaultProps: {
					size: 'small',
				},
				styleOverrides: {
					root: {
						padding: 6,
					},
				},
			},
			MuiRadio: {
				defaultProps: {
					size: 'small',
				},
				styleOverrides: {
					root: {
						padding: 6,
					},
				},
			},
			MuiFormLabel: {
				styleOverrides: {
					root: {
						fontWeight: 600,
						fontSize: '14px',
						color: strongText,
					},
					minimal: minimalLabelTheme,
				},
			},
			MuiFormHelperText: {
				styleOverrides: {
					root: {
						marginLeft: '14px',
					},
				},
			},
			MuiChip: {
				styleOverrides: {
					// `root` would win over MUI's own colorPrimary/colorSuccess/etc. backgrounds
					// (theirs are baked into the base styles, applied before styleOverrides.root).
					// `colorDefault` only applies when no `color` prop is set, so it's the correct
					// scoped slot for "the colourless default chip".
					colorDefault: {
						...(mode === 'dark' && {
							backgroundColor: palette.darkModeChip,
						}),
					},
				},
			},
			MuiCard: {
				variants: [
					{
						props: { variant: 'about-card' },
						style: ({ theme }) => ({
							padding: theme.spacing(2, 3),
							borderTopRightRadius: 0,
							borderTopLeftRadius: 0,
							borderBottomLeftRadius: 8,
							borderLeft: `1px solid ${theme.palette.divider}`,
							borderBottom: `1px solid ${theme.palette.divider}`,
							...(theme.palette.mode === 'dark' && {
								backgroundColor: palette.darkModeBackground,
							}),
						}),
					},
				],
			},
			MuiListItemButton: {
				variants: [
					{
						props: { variant: 'side-nav' },
						style: ({ theme }) => sideNavListItemStyle(theme, palette.folioBlue),
					},
					{
						props: { variant: 'side-nav-winbeat' },
						style: ({ theme }) => sideNavListItemStyle(theme, palette.winbeatOrange),
					},
				],
			},
			MuiDataGrid: {
				styleOverrides: {
					root: {
						// DataGrid derives its cell/row border colour by darkening `divider` by
						// 68% internally, which crushes our (already dark) divider to near-black.
						// Override the CSS var it reads directly instead of fighting that.
						...(mode === 'dark' && {
							'--DataGrid-rowBorderColor': palette.darkModeDivider,
						}),
						// Column headers, the columns/export toolbar, and any sticky cells that
						// match the header (e.g. pinned action columns) all read this same var —
						// override it once here instead of chasing each consumer individually.
						...(mode === 'dark' && {
							'--DataGrid-containerBackground': palette.darkModeHeader,
						}),
						// The footer's top border (and anything else using this shared class)
						// reads a hardcoded borderColor baked in at theme-creation time, not the
						// CSS var above, so it needs its own override.
						...(mode === 'dark' && {
							'& .MuiDataGrid-withBorderColor': {
								borderColor: palette.darkModeDivider,
							},
						}),
						// The resizable column separator's colour is likewise baked in at
						// theme-creation time (darken(divider, 0.68)), not read from a CSS var.
						...(mode === 'dark' && {
							'& .MuiDataGrid-columnSeparator': {
								color: palette.darkModeDivider,
							},
						}),
						'& .MuiDataGrid-editInputCell': {
							backgroundColor: 'transparent',
						},
						'& .pinned-column': {
							position: 'sticky',
							right: 0,
							background: mode === 'dark' ? neutral[800] : '#fff',
							zIndex: 3,
							boxShadow: '-3px 0 5px -2px rgba(0,0,0,0.1)',
						},
						// Remove boxShadow from all other siblings
						'& .pinned-column ~ .pinned-column': {
							boxShadow: 'none',
						},
						'& .MuiDataGrid-columnHeader-Title': {
							fontWeight: 600,
							textTransform: 'uppercase',
						},
						'& .MuiDataGrid-columnHeaderTitle': {
							fontWeight: 600,
							textTransform: 'uppercase',
						},
						// Match the column headers' background exactly, via the same CSS var DataGrid uses for them.
						'& .MuiDataGrid-toolbarContainer': {
							backgroundColor: 'var(--DataGrid-containerBackground)',
						},
					},
					row: {
						...(mode === 'dark' && {
							backgroundColor: palette.darkModePaper,
						}),
						'&.Mui-selected': {
							color: primaryMain,
						},
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						...(mode === 'dark' && {
							backgroundColor: palette.darkModePaper,
							// MUI applies a lightening gradient per elevation level in dark mode by
							// default — disable it so every Paper stays a flat, consistent colour.
							backgroundImage: 'none',
						}),
					},
				},
			},
			// Dropdown/menu surfaces (Select, Autocomplete, context menus) render a Paper
			// too, but should read as an extension of the input they came from rather than
			// a generic elevated surface — so they match the TextField colour, not MuiPaper's.
			MuiPopover: {
				styleOverrides: {
					paper: {
						...(mode === 'dark' && {
							backgroundColor: palette.darkModeTextField,
							backgroundImage: 'none',
						}),
					},
				},
			},
			MuiMenu: {
				styleOverrides: {
					paper: {
						...(mode === 'dark' && {
							backgroundColor: palette.darkModeTextField,
							backgroundImage: 'none',
						}),
					},
				},
			},
			MuiAutocomplete: {
				styleOverrides: {
					paper: {
						...(mode === 'dark' && {
							backgroundColor: palette.darkModeTextField,
							backgroundImage: 'none',
						}),
					},
					root: {
						'& .MuiOutlinedInput-root': {
							minHeight: 35,
							height: 35,
							paddingTop: 0,
							paddingBottom: 0,
							alignItems: 'center',
							flexWrap: 'nowrap',
						},
					},
					input: {
						paddingTop: 6,
						paddingBottom: 6,
					},
				},
			},
			MuiTableRow: {
				styleOverrides: {
					root: {
						...(mode === 'dark' && {
							backgroundColor: palette.darkModePaper,
						}),
					},
				},
			},
			MuiTable: {
				styleOverrides: {
					root: ({ theme }) => ({
						'& .MuiTableCell-root': {
							borderColor: theme.palette.divider,
							padding: '8px 10px',
						},
						'& tbody tr:last-of-type .MuiTableCell-root': {
							borderBottom: 'none',
						},
					}),
				},
			},
			MuiTableHead: {
				styleOverrides: {
					root: ({ theme }) => ({
						height: '56px',
						'& .MuiTableCell-root': {
							fontWeight: 600,
							textTransform: 'uppercase',
						},
						'& .MuiTableRow-root': {
							backgroundColor: theme.palette.background.default,
						},
					}),
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: ({ theme }) => ({
						...(theme.palette.mode === 'dark' && {
							border: `1px solid ${theme.palette.divider}`,
						}),
					}),
				},
			},
			MuiDialogActions: {
				styleOverrides: {
					root: {
						backgroundColor:
							mode === 'dark' ? palette.darkModeTextField : palette.dialogActions,
						p: 1.5,
					},
				},
			},
			MuiAlert: {
				styleOverrides: {
					// Border colour matches MUI's own `variant="outlined"` Alert border
					// (theme.palette[color].light), so it reads as the colour's identity.
					standardError: ({ theme }) => ({
						...(theme.palette.mode === 'dark' && {
							backgroundColor: palette.darkModeSideNav,
							border: `1.5px solid ${theme.palette.error.light}`,
							color: '#fff',
						}),
					}),
					standardSuccess: ({ theme }) => ({
						...(theme.palette.mode === 'dark' && {
							backgroundColor: palette.darkModeSideNav,
							border: `1.5px solid ${theme.palette.success.light}`,
							color: '#fff',
						}),
					}),
				},
			},
			MuiSnackbarContent: {
				styleOverrides: {
					root: ({ theme }) => ({
						...(theme.palette.mode === 'dark' && {
							backgroundColor: palette.darkModeSideNav,
							border: `1.5px solid ${palette.folioBlue}`,
							color: '#fff',
						}),
					}),
				},
			},
		},
	});
};
